import { useEffect, useId, useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, DragEvent } from 'react';

import { PAYMENT_INVOICE_ACCEPT } from '../../../api/patient-payments';
import { Icon } from '../../../components/Icon';
import styles from './PatientPaymentsPanel.module.scss';

/** First file carried by a drop or a paste, whichever shape the browser used. */
function firstFileFrom(data: DataTransfer | null): File | null {
  if (!data) return null;
  const [transferred] = Array.from(data.files ?? []);
  if (transferred) return transferred;
  for (const item of Array.from(data.items ?? [])) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file) return file;
  }
  return null;
}

export type InvoiceDropZoneProps = {
  readonly label: string;
  readonly hint: string;
  readonly dropHint: string;
  readonly emptyLabel: string;
  readonly clearLabel: string;
  readonly file: File | null;
  readonly disabled?: boolean;
  /** File-picker accept list; defaults to every supported invoice format. */
  readonly accept?: string;
  /**
   * Listen for Ctrl+V anywhere on the page instead of only while the field
   * holds focus. Only the create form sets this: several mounted zones all
   * claiming the document paste would race for the same clipboard file.
   */
  readonly captureDocumentPaste?: boolean;
  readonly onFilePicked: (file: File | null) => void;
};

/**
 * Invoice attachment field: click to browse, drag a file onto it, or paste one
 * with Ctrl+V.
 *
 * Drag depth is counted rather than toggled, because `dragenter`/`dragleave`
 * also fire for the children and a plain boolean would clear the highlight
 * while the pointer is still over the zone.
 */
export function InvoiceDropZone({
  label,
  hint,
  dropHint,
  emptyLabel,
  clearLabel,
  file,
  disabled = false,
  accept = PAYMENT_INVOICE_ACCEPT,
  captureDocumentPaste = false,
  onFilePicked,
}: InvoiceDropZoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);
  const [isDropActive, setDropActive] = useState(false);

  // Kept in a ref so the document listener is not re-subscribed on every
  // render of the parent form.
  const onFilePickedRef = useRef(onFilePicked);
  useEffect(() => {
    onFilePickedRef.current = onFilePicked;
  }, [onFilePicked]);

  useEffect(() => {
    if (!captureDocumentPaste || disabled) return undefined;
    function handleDocumentPaste(event: globalThis.ClipboardEvent) {
      const pasted = firstFileFrom(event.clipboardData);
      if (!pasted) return;
      event.preventDefault();
      onFilePickedRef.current(pasted);
    }
    document.addEventListener('paste', handleDocumentPaste);
    return () => document.removeEventListener('paste', handleDocumentPaste);
  }, [captureDocumentPaste, disabled]);

  function pick(picked: File | null) {
    if (inputRef.current) inputRef.current.value = '';
    onFilePicked(picked);
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    if (disabled) return;
    const pasted = firstFileFrom(event.clipboardData);
    if (!pasted) return;
    event.preventDefault();
    // Stops the document-level listener from handling the same clipboard file.
    event.stopPropagation();
    pick(pasted);
  }

  function handleDragEnter(event: DragEvent<HTMLLabelElement>) {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setDropActive(true);
  }

  function handleDragLeave(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDropActive(false);
    }
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    event.dataTransfer.dropEffect = 'copy';
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setDropActive(false);
    if (disabled) return;
    const dropped = firstFileFrom(event.dataTransfer);
    if (dropped) pick(dropped);
  }

  return (
    <div className={`${styles.field} ${styles.fieldFull}`} onPaste={handlePaste}>
      <label
        className={styles.dropZone}
        htmlFor={inputId}
        data-active={isDropActive ? 'true' : undefined}
        data-has-file={file ? 'true' : undefined}
        data-disabled={disabled ? 'true' : undefined}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <span className={styles.dropZoneText}>
          <span className={styles.dropZoneLabel}>{label}</span>
          <span className={styles.dropZonePrimary}>{file ? file.name : emptyLabel}</span>
          <span className={styles.dropZoneHint}>{dropHint}</span>
        </span>
        <span className={styles.dropZoneIcon} aria-hidden>
          <Icon name="attach" size={20} />
        </span>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept={accept}
          className={styles.visuallyHiddenInput}
          disabled={disabled}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            pick(event.target.files?.[0] ?? null)
          }
        />
      </label>
      {file ? (
        <button
          type="button"
          className={styles.dropZoneClear}
          onClick={() => pick(null)}
          disabled={disabled}
        >
          {clearLabel}
        </button>
      ) : null}
      <small>{hint}</small>
    </div>
  );
}
